import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TenderVote, VoteType } from '@/actions/dtos/tender/vote';
import { useAccount } from '@/hooks/useAccount';
import { faThumbsDown, faThumbsUp } from '@fortawesome/pro-solid-svg-icons';
import { useVoteStats } from '@/utils/voteUtils';
import { formatDateShort } from '@/utils/formatDate';
import { useTranslations } from 'next-intl';

interface TeamVotesSectionProps {
  votes: TenderVote[];
}

const TeamVotesSection: React.FC<TeamVotesSectionProps> = ({ votes }) => {
  const { member } = useAccount();
  const { positiveCount, negativeCount, hasUserPositiveVote, hasUserNegativeVote, getVoteDisplay } = useVoteStats(
    votes,
    member?.id,
  );
  const t = useTranslations();

  return votes.length === 0 ? (
    <p className="py-1 text-center text-sm">{t('TenderSidebar_Votesection_Empty')}</p>
  ) : (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2.5">
        <div className="flex items-center space-x-1 rounded-lg bg-base-200 px-3 py-2">
          <FontAwesomeIcon icon={faThumbsUp} />
          <span className="text-sm">{getVoteDisplay(positiveCount, hasUserPositiveVote)}</span>
        </div>
        <div className="flex items-center space-x-1 rounded-lg bg-base-200 px-3 py-2">
          <FontAwesomeIcon icon={faThumbsDown} />
          <span className="text-sm">{getVoteDisplay(negativeCount, hasUserNegativeVote)}</span>
        </div>
      </div>
      <div className="flex max-h-3xs flex-col gap-3 overflow-y-auto rounded-lg bg-base-200 p-3">
        {votes
          .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())
          .map((vote) => (
            <div key={vote.id} className="rounded-lg bg-base-300 p-4">
              <div className="mb-2 break-words">
                <span>
                  {vote.voter.person.firstname} {vote.voter.person.lastname}
                </span>
              </div>
              <div className="mb-2 flex items-center space-x-2">
                <FontAwesomeIcon icon={vote.type === VoteType.POSITIVE ? faThumbsUp : faThumbsDown} />
                <span className="text-sm">{formatDateShort(vote.created)}</span>
              </div>
              {vote.reason && <div className="break-words text-sm font-light">{vote.reason}</div>}
            </div>
          ))}
      </div>
    </div>
  );
};

export default TeamVotesSection;
